import {
  CalendarDays,
  Clock,
  Truck,
  Package,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react'
import { RolePageData } from '../types'

export const eventsData: RolePageData = {
  id: 'events',
  slug: 'events',
  label: 'Event Managers',
  icon: CalendarDays,
  title: 'Get the right merch to the right place, on time',
  description:
    'Deadlines, venue logistics, and last-minute changes handled with confidence.',

  context:
    'Often under marketing, with time-bound projects. Success is measured by flawless execution.',

  jobToBeDone: 'Get the right merch to the right place, on time.',

  dailyReality: [
    'Hard deadlines that cannot slip',
    'Shipping to venues and event locations',
    'Last-minute changes from stakeholders',
    'Quantity planning with limited information',
  ],

  painPoints: [
    {
      title: 'Deadline pressure',
      description:
        'Events have hard dates. If merch arrives late, there is no recovery.',
      icon: Clock,
    },
    {
      title: 'Venue shipping complexity',
      description:
        'Shipping to hotels, convention centers, and temporary venues has its own challenges.',
      icon: Truck,
    },
    {
      title: 'Last-minute changes',
      description:
        'Attendee counts change. Branding updates come late. Flexibility is essential.',
      icon: RefreshCw,
    },
    {
      title: 'Quantity mistakes',
      description:
        'Order too few and you run out. Order too many and budget is wasted.',
      icon: Package,
    },
  ],

  hiddenFears: [
    'Late delivery ruins the event',
    'Missing items when we need them',
    'Wrong print or wrong product arrives',
  ],

  esgAngle: {
    level: 'medium',
    description:
      'Medium sensitivity. Cares about waste from over-ordering and quality that reflects well on the event.',
  },

  whatTheyValue: ['Speed', 'Reliability', 'Clarity'],

  languageTheyUse: [
    'Deadlines',
    'Logistics',
    'Quantities',
    'Delivery dates',
    'Venue',
  ],

  convincers: [
    {
      quote: 'Guaranteed delivery dates',
      description:
        'We commit to delivery windows and hit them. You get tracking and proactive updates.',
    },
    {
      quote: 'Venue-ready shipping',
      description:
        'We know how to ship to event venues, including labeling, timing, and contact coordination.',
    },
    {
      quote: 'Flexible until the last moment',
      description:
        'Need to adjust quantities or items? We work with you until production locks.',
    },
    {
      quote: 'Rush options when plans change',
      description:
        'Fast-track production available when timelines compress unexpectedly.',
    },
  ],

  dealBreakers: [
    {
      title: 'Missed deadlines',
      description:
        'If we can\'t guarantee delivery timing, we shouldn\'t take the order.',
    },
    {
      title: 'Inflexible processes',
      description:
        'Events change. If we can\'t adapt, we\'re not the right partner.',
    },
    {
      title: 'Poor communication',
      description:
        'You need proactive updates, not silence. We keep you informed at every step.',
    },
  ],

  testimonial: {
    headline: 'Conference merch delivered perfectly, two days early',
    name: 'Lisa Andersson',
    title: 'Event Lead, Nordic Summit',
    image:
      'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&h=400&fit=crop&crop=face',
  },

  faqs: [
    {
      question: 'Can you ship directly to event venues?',
      answer:
        'Yes. We handle venue shipping regularly, including specific delivery windows, labeling requirements, and on-site contact coordination.',
    },
    {
      question: 'What if attendee numbers change close to the event?',
      answer:
        'We build in flexibility where possible. You can adjust quantities up to a certain point before production locks. We\'ll always be clear about cutoff dates.',
    },
    {
      question: 'How do rush orders work?',
      answer:
        'We offer expedited production for urgent needs. Timelines and pricing vary by product, but we\'ll give you clear options fast.',
    },
    {
      question: 'What happens if something goes wrong?',
      answer:
        'We have contingency plans and backup suppliers for critical orders. If there\'s an issue, we communicate immediately and present solutions.',
    },
  ],

  cta: {
    primary: 'Get an event-ready merch quote',
    secondary: 'See how event logistics work',
    promptTemplate:
      "Hi! I'm planning an event and need merch that will be delivered on time to our venue. Reliable delivery is critical. Can you help me get a quote for event-ready merchandise?",
  },
}
